import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { revieAdultValidationSchema } from "../../validators/LapsedPolicy.ts";
import { usePostReviveAdultPolicy } from "../../hooks/lapsed-policy.ts";
import {
  calculateBMI,
  convertToCm,
  toastError,
  toastSuccess,
  UserToken,
} from "../../utils/helper.js";
import { useEffect } from "react";
import ReviveAdultPolicyForm from "./ReviveAdultPolicyForm.tsx";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

type ReviveAdultPolicyResponse = {
  code?: string;
  message?: string;
  [key: string]: any;
};

const LifeInsuranceReactivationForm = ({
  policyNo,
  gender,
}: {
  policyNo: string;
  gender: string;
}) => {
  const navigate = useNavigate();
  const { mutateAsync } = usePostReviveAdultPolicy();

  const form = useForm({
    defaultValues: {
      height: "",
      weight: "",
      hasDisease: undefined,
      hasSurgery: undefined,
      hasCovid: undefined,
      isHealthy: undefined,
      isPregnant: undefined,
      hasAbortion: undefined,
      hasChildren: undefined,
      deliveryType: "",
      declaration: undefined,
      heightFeet: undefined,
      heightInch: undefined,
      dateOfAbortion: undefined,
      dateOfPregnancy: undefined,
    },
    resolver: yupResolver(revieAdultValidationSchema),
    mode: "onChange",
  });

  const { errors } = form.formState;

  const isPregnant = form.watch("isPregnant");
  const hasChildren = form.watch("hasChildren") === ("true" as unknown);
  const isNormalDelivery = form.watch("deliveryType") === "normal";
  const hasAbortion = form.watch("hasAbortion") === ("true" as unknown);

  const onSubmit = async (data: any) => {
    // const heightCm = convertToCm(data.heightFeet, data.heightInch);
    data.height = data.heightFeet +"."+ data.heightInch;

    if (!hasChildren) {
      data.deliveryMethod = undefined;
    }
    if (!hasAbortion) {
      data.dateOfAbortion = undefined;
    }
    if (isPregnant === ("true" as unknown)) {
      data.hasAbortion = undefined;
      data.deliveryMethod = undefined;
      data.dateOfPregnancy = undefined;
    }

    const formData = { policyNo: policyNo, medicalDetail: data };

    const formDatastringify = JSON.stringify(
      formData,
      (key, value) => {
        if (typeof value === "boolean" || typeof value === "number") {
          return String(value); // convert to string
        }
        return value; // keep other types unchanged
      },
      2
    );

    await UserToken();
    const response = (await mutateAsync({
      payload: formDatastringify,
      url: "LapsedPolicy/RegisterLapsedPolicy",
    })) as ReviveAdultPolicyResponse;

    if (response?.code !== "100") {
      toastError(
        response?.message || "Failed to revive policy. Please try again."
      );
      return;
    }
    Swal.fire({
      title: "Success!",
      text: response?.message,
      icon: "success",
      confirmButtonColor: "#E51D25",
    } as any).then((result) => {
      if (result.isConfirmed) {
        navigate("/lapsed-policies");
      }
    });
    // toastSuccess("Policy revived successfully.");
  };

  const heightInch = form.watch("heightInch");

  useEffect(() => {
    form.setValue(
      "height",
      String(convertToCm(form.getValues("heightFeet"), heightInch))
    );
  }, [heightInch]);

  useEffect(() => {
    form.setValue("dateOfPregnancy", "");
  }, [isNormalDelivery]);

  const height = form.watch("heightFeet");
  const weight = form.watch("weight");

  const bmi = calculateBMI(height, heightInch, weight);

  useEffect(() => {
    if (bmi) {
      form.setValue("bmi", Number(bmi.toFixed(2)));
      form.trigger("bmi");
      const isNormalBMI = bmi >= 13.5 && bmi <= 24.9;

      if (!isNormalBMI) {
        Swal.fire({
          title: "Error",
          html: "तपाईं अगाडि बढ्न सक्नुहुन्न। सहायता को लागि कृपया नजिकको शाखामा वा ०१-५९७१४३३ / ९८०१९६६६३० मा सम्पर्क गर्नुहोस्।",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  }, [bmi]);

  const deliveryMethod = form.watch("deliveryType");
  useEffect(() => {
    form.resetField("dateOfPregnancy");
  }, [deliveryMethod]);
  return (
    <div className="max-w-4xl mx-auto p-2 md:p-5 bg-white">
      <h4 className="text-xl font-semibold text-center underline mb-4">
        विषयः जीवन बीमालेख पुनर्जागरण गराइदिने बारे ।
      </h4>

      <p>
        उपरोक्त सम्बन्धमा मेरो / बीमा चाहने व्यक्तिको जीवन बीमालेखमा भुक्तानी
        गर्नुपर्ने नविकरण बीमाशुल्क विभिन्न कारणले तोकिएको समयमा भुक्तान गर्न
        नसकिएकाले बीमालेख व्यतित भएको छ। अतः सो व्यतित बीमालेख कम्पनीको
        नियमानुसार पुनर्जागरण गरिदिनुहुन अनुरोध गर्दछु।
      </p>
      <FormProvider {...form}>
        <ReviveAdultPolicyForm
          gender={gender}
          onSubmit={onSubmit}
          hadMiscarriage={hasAbortion}
          hasChildren={hasChildren}
          isNormalDelivery={isNormalDelivery}
          isPregnant={isPregnant}
        />
      </FormProvider>
    </div>
  );
};

export default LifeInsuranceReactivationForm;
